import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Shield, Users as UsersIcon, FileText, Pencil, Globe, Trash2 } from "lucide-react";
import AdminShell, { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs } from "@/components/admin/SubTabs";
import IpAccessLogsPanel from "@/components/admin/IpAccessLogsPanel";
import { api, getErrorMessage } from "@/lib/api";
import { useAdminAuth } from "@/lib/adminAuth";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";
import { formatAuditAction, formatAuditTarget } from "@/utils/adminDisplay";

const ASSIGNABLE_ROLES = [
  { id: "compliance_officer", label: "Compliance Officer", desc: "KYC, AML, FIU, audit logs" },
  { id: "support_admin", label: "Support Admin", desc: "Tickets and user lookups" },
  { id: "auditor", label: "Read-Only Auditor", desc: "View-only access for audits" },
];

const ROLES = [
  { id: "super_admin", label: "Super Admin", desc: "Full access — role is permanent" },
  ...ASSIGNABLE_ROLES,
];

function isSuperAdmin(a) {
  return (a.admin_role || "") === "super_admin";
}

const TABS = [
  { id: "admins", label: "Admin Users", icon: UsersIcon },
  { id: "roles", label: "Roles & Permissions", icon: Shield },
  { id: "ip_logs", label: "IP Access Logs", icon: Globe },
  { id: "audit", label: "Activity Logs", icon: FileText },
];

const emptyIpForm = () => ({
  email: "",
  full_name: "",
  role: "support_admin",
  password: "",
  ip_restriction_enabled: false,
  allowed_ips: [],
});

export default function AccessControl() {
  const { admin: me } = useAdminAuth();
  const iAmSuperAdmin = isSuperAdmin(me || {});
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "admins";
  const [tab, setTab] = useState(initialTab);
  const [admins, setAdmins] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [adminStats, setAdminStats] = useState({ total: 0, active: 0, ipRestricted: 0 });
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState("");
  const adminsPager = useAdminPager();
  const auditPager = useAdminPager();

  async function loadAdmins() {
    setAdminsLoading(true);
    try {
      const { data } = await api.get("/api/admin/admins", {
        params: { skip: adminsPager.skip, limit: adminsPager.limit },
      });
      const { items, total, extra } = unwrapAdminList(data, "admins");
      setAdmins(items);
      adminsPager.setTotal(total);
      setAdminStats({
        total,
        active: Number(extra?.active_count ?? items.filter((a) => (a.status || "active") === "active").length),
        ipRestricted: Number(extra?.ip_restricted_count ?? items.filter((a) => a.ip_restriction_enabled).length),
      });
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setAdminsLoading(false);
    }
  }

  async function loadAudit() {
    setAuditLoading(true);
    try {
      const { data } = await api.get("/api/admin/audit-log", {
        params: { skip: auditPager.skip, limit: auditPager.limit },
      });
      const { items, total } = unwrapAdminList(data, "logs");
      setAuditLog(items);
      auditPager.setTotal(total);
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setAuditLoading(false);
    }
  }

  function load() {
    loadAdmins();
    if (tab === "audit") loadAudit();
  }

  useEffect(() => {
    loadAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminsPager.page]);

  useEffect(() => {
    if (tab === "audit") loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, auditPager.page]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  async function setRole(id, role) {
    if (!iAmSuperAdmin) return;
    try {
      await api.patch(`/api/admin/admins/${id}/role`, { role });
      load();
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  function canModifyRow(a) {
    if (!iAmSuperAdmin) return false;
    return true;
  }

  async function deactivate(id) {
    if (!iAmSuperAdmin) return;
    if (!window.confirm("Deactivate this admin?")) return;
    try {
      await api.patch(`/api/admin/admins/${id}`, { status: "suspended" });
      loadAdmins();
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  async function deleteAdmin(id) {
    if (!iAmSuperAdmin) return;
    if (id === me?.id) return;
    if (!window.confirm("Permanently delete this admin account? This cannot be undone.")) return;
    try {
      await api.delete(`/api/admin/admins/${id}`, { params: { permanent: true } });
      loadAdmins();
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  return (
    <AdminShell title="Access control" subtitle="Admin users, roles, and per-admin IP restrictions">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Total Admins" value={adminStats.total} icon={UsersIcon} variant="yellow" />
        <KpiCard
          label="IP Restricted"
          value={adminStats.ipRestricted}
          icon={Globe}
          variant="purple"
        />
        <KpiCard
          label="Active"
          value={adminStats.active}
          icon={UsersIcon}
          variant="green"
        />
        <KpiCard label="Roles defined" value={ROLES.length} icon={Shield} variant="blue" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="ac-tab" />

      {tab === "admins" && (
        <Panel className="mt-5">
          <div className="p-4 border-b border-line flex justify-between">
            <div className="text-[14px] font-semibold">Admin Users</div>
            {iAmSuperAdmin ? (
              <Btn onClick={() => setModal({ mode: "create" })} data-testid="add-admin">
                <Plus size={14} /> Add Admin
              </Btn>
            ) : null}
          </div>
          <table className="w-full text-[13px]" data-testid="admins-table">
            <thead className="bg-background text-muted-foreground text-left text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">IP lock</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {admins.map((a) => (
                <tr key={a.id} className="hover:bg-surface-2" data-testid={`admin-row-${a.id}`}>
                  <td className="px-5 py-3 font-semibold">{a.email}</td>
                  <td className="px-5 py-3 text-muted-foreground">{a.full_name || "—"}</td>
                  <td className="px-5 py-3">
                    {isSuperAdmin(a) ? (
                      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary">
                        <Shield size={12} /> Super Admin
                      </span>
                    ) : iAmSuperAdmin ? (
                      <select
                        value={a.admin_role || "support_admin"}
                        onChange={(e) => setRole(a.id, e.target.value)}
                        className="bg-background border border-line rounded-md px-2 py-1 text-[12px]"
                      >
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-[12px] text-muted-foreground capitalize">
                        {(a.admin_role || "admin").replace(/_/g, " ")}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    {a.ip_restriction_enabled ? (
                      <span className="text-[11px] text-primary">
                        On ({(a.allowed_ips || []).length} IP{(a.allowed_ips || []).length === 1 ? "" : "s"})
                      </span>
                    ) : (
                      <span className="text-[11px] text-dim">Off</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill value={a.status || "active"} />
                  </td>
                  <td className="px-5 py-3 text-right space-x-3">
                    {canModifyRow(a) && !isSuperAdmin(a) ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setModal({ mode: "edit", admin: a })}
                          className="text-primary text-[12px] hover:underline inline-flex items-center gap-1"
                          data-testid={`edit-admin-${a.id}`}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deactivate(a.id)}
                          className="text-down text-[12px] hover:underline"
                        >
                          Deactivate
                        </button>
                        {a.id !== me?.id ? (
                          <button
                            type="button"
                            onClick={() => deleteAdmin(a.id)}
                            className="text-down text-[12px] hover:underline inline-flex items-center gap-1"
                            data-testid={`delete-admin-${a.id}`}
                          >
                            <Trash2 size={12} /> Delete
                          </button>
                        ) : null}
                      </>
                    ) : canModifyRow(a) && isSuperAdmin(a) ? (
                      <button
                        type="button"
                        onClick={() => setModal({ mode: "edit", admin: a })}
                        className="text-primary text-[12px] hover:underline inline-flex items-center gap-1"
                        data-testid={`edit-admin-${a.id}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                    ) : (
                      <span className="text-[11px] text-dim">View only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <AdminPager
            page={adminsPager.page}
            total={adminsPager.total}
            pageSize={adminsPager.pageSize}
            loading={adminsLoading}
            onPage={adminsPager.setPage}
            testId="admins-pager"
          />
        </Panel>
      )}

      {tab === "roles" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          {ROLES.map((r) => (
            <Panel key={r.id} className="p-5" data-testid={`role-${r.id}`}>
              <div className="text-[14px] font-semibold">{r.label}</div>
              <div className="text-[12px] text-muted-foreground mt-1">{r.desc}</div>
            </Panel>
          ))}
        </div>
      )}

      {tab === "ip_logs" && (
        <div className="mt-5">
          <IpAccessLogsPanel />
        </div>
      )}

      {tab === "audit" && (
        <Panel className="mt-5">
          <table className="w-full text-[13px]">
            <thead className="bg-background text-muted-foreground text-left text-[11px] uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3">Time</th>
                <th className="px-5 py-3">Admin</th>
                <th className="px-5 py-3">Action</th>
                <th className="px-5 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {auditLog.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-10 text-center text-muted-foreground">
                    No audit log entries yet.
                  </td>
                </tr>
              ) : (
                auditLog.map((a) => (
                  <tr key={a.id}>
                    <td className="px-5 py-3 text-muted-foreground">{new Date(a.at).toLocaleString()}</td>
                    <td className="px-5 py-3">{a.admin_email || a.admin_id}</td>
                    <td className="px-5 py-3">{formatAuditAction(a.action)}</td>
                    <td className="px-5 py-3 text-muted-foreground text-[11px]">
                      {formatAuditTarget(a.action, a.target)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <AdminPager
            page={auditPager.page}
            total={auditPager.total}
            pageSize={auditPager.pageSize}
            loading={auditLoading}
            onPage={auditPager.setPage}
            testId="audit-pager"
          />
        </Panel>
      )}

      {modal && iAmSuperAdmin && (
        <AdminFormModal
          mode={modal.mode}
          admin={modal.admin}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
          }}
        />
      )}
      {err && (
        <div className="fixed bottom-5 right-5 bg-down text-white px-4 py-3 rounded-lg text-[13px] z-[200]">
          {err}
        </div>
      )}
    </AdminShell>
  );
}

function AdminFormModal({ mode, admin, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const targetIsSuper = isEdit && admin && isSuperAdmin(admin);
  const [form, setForm] = useState(() => {
    if (isEdit && admin) {
      return {
        email: admin.email,
        full_name: admin.full_name || "",
        role: admin.admin_role || "support_admin",
        password: "",
        ip_restriction_enabled: !!admin.ip_restriction_enabled,
        allowed_ips: [...(admin.allowed_ips || [])],
      };
    }
    return emptyIpForm();
  });
  const [newIp, setNewIp] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  function addIp() {
    const v = newIp.trim();
    if (!v) return;
    if (form.allowed_ips.includes(v)) return;
    setForm((f) => ({ ...f, allowed_ips: [...f.allowed_ips, v] }));
    setNewIp("");
  }

  function removeIp(ip) {
    setForm((f) => ({ ...f, allowed_ips: f.allowed_ips.filter((x) => x !== ip) }));
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          full_name: form.full_name,
          ip_restriction_enabled: form.ip_restriction_enabled,
          allowed_ips: form.allowed_ips,
        };
        if (!targetIsSuper) payload.role = form.role;
        await api.patch(`/api/admin/admins/${admin.id}`, payload);
      } else {
        await api.post("/api/admin/admins/invite", {
          email: form.email,
          full_name: form.full_name,
          role: form.role,
          password: form.password,
          ip_restriction_enabled: form.ip_restriction_enabled,
          allowed_ips: form.allowed_ips,
        });
      }
      onSaved();
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-surface border border-line rounded-xl w-full max-w-[520px] p-5 space-y-3 max-h-[90vh] overflow-y-auto"
        data-testid="admin-form-modal"
      >
        <div className="flex justify-between items-center">
          <h3 className="text-[16px] font-semibold">{isEdit ? "Edit Admin" : "Add Admin"}</h3>
          <button type="button" onClick={onClose} className="text-muted-foreground text-xl">
            ×
          </button>
        </div>

        {!isEdit && (
          <input
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full h-[40px] px-3 bg-background border border-line rounded-md text-[13px]"
          />
        )}
        <input
          required
          placeholder="Full name"
          value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          className="w-full h-[40px] px-3 bg-background border border-line rounded-md text-[13px]"
        />
        {targetIsSuper ? (
          <div className="flex items-center gap-2 h-[40px] px-3 bg-background border border-line rounded-md text-[13px] text-primary font-semibold">
            <Shield size={14} /> Super Admin — role cannot be changed
          </div>
        ) : (
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="w-full h-[40px] px-3 bg-background border border-line rounded-md text-[13px]"
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        )}
        {!isEdit && (
          <input
            required
            type="password"
            placeholder="Initial password (8+ chars)"
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full h-[40px] px-3 bg-background border border-line rounded-md text-[13px]"
          />
        )}

        <div className="border border-line rounded-lg p-4 space-y-3">
          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input
              type="checkbox"
              checked={form.ip_restriction_enabled}
              onChange={(e) => setForm({ ...form, ip_restriction_enabled: e.target.checked })}
              data-testid="admin-ip-restriction-toggle"
            />
            <span className="font-semibold">Enable IP restriction</span>
          </label>
          <p className="text-[11px] text-muted-foreground">
            When enabled, this admin may only sign in and use the panel from listed IPs/CIDRs.
            Leave the list empty to allow any IP.
          </p>
          <div className="flex gap-2">
            <input
              placeholder="IP or CIDR e.g. 192.168.1.0/24"
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              className="flex-1 h-[36px] px-3 bg-background border border-line rounded-md text-[13px] font-mono"
            />
            <Btn type="button" onClick={addIp}>
              <Plus size={14} /> Add
            </Btn>
          </div>
          {form.allowed_ips.length > 0 ? (
            <ul className="space-y-1">
              {form.allowed_ips.map((ip) => (
                <li
                  key={ip}
                  className="flex justify-between items-center text-[12px] font-mono bg-background px-3 py-2 rounded-md"
                >
                  {ip}
                  <button
                    type="button"
                    onClick={() => removeIp(ip)}
                    className="text-down text-[11px]"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[11px] text-dim">No IPs added — all addresses allowed when restriction is on.</p>
          )}
        </div>

        {err && <div className="text-[12px] text-down">{err}</div>}
        <Btn type="submit" className="w-full" disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create Admin"}
        </Btn>
      </form>
    </div>
  );
}
